import { css, StyleDeclarationValue } from 'aphrodite';
import { Separator, ToggleGroup } from 'radix-ui';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { FabContext } from '../../components/FabContext';
import { Portal, PortalDestination } from '../../components/Portal';
import { SparklesIcon } from '../../components/svgs/icons/SparklesIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { AppDrawerFab } from './AppDrawerFab';
import { styleSheet } from './styles';

export const LOCAL_STORAGE_ACTIVE_APP_DRAWER_KEY = 'active-app-drawer-key';
const LOCAL_STORAGE_COFFEE_ACTIVE_DRAWER_KEY = 'coffee-active-coffee-drawer-key';
const LOCAL_STORAGE_ADMIN_ACTIVE_DRAWER_KEY = 'admin-active-admin-drawer-key';
const LOCAL_STORAGE_APP_DRAWER_WIDTH_KEY = 'app-drawer-width';

const MIN_DRAWER_WIDTH = 400;
const MAX_DRAWER_WIDTH = 800;

const AppDrawerContext = createContext<{
	value: string;
	setValue: (value: string) => void | null;
	width: number;
	setWidth: (width: number) => void | null;
}>({
	value: '',
	setValue: null,
	width: MIN_DRAWER_WIDTH,
	setWidth: null,
});

export const useAppDrawerContext = () => {
	const context = useContext(AppDrawerContext);
	if (!context) {
		throw new Error('useAppDrawerContext must be used within AppDrawer');
	}
	return context;
};

const appDrawerPortalId = 'app-drawer-portal-id';
const coffeeDrawerPortalId = 'coffee-drawer-portal-id';

export const AppDrawerPortalDestination = () => {
	return <PortalDestination id={appDrawerPortalId} className={css(styleSheet.drawerGridArea)} />;
};

export const CoffeeDrawerPortalDestination = () => {
	return <PortalDestination id={coffeeDrawerPortalId} className={css(styleSheet.drawerGridArea)} />;
};

interface AppDrawerProps {
	children: ReactNode;
	styles?: StyleDeclarationValue[];
	value?: string;
	onValueChange?: (value: string) => void;
}

export const SideDrawer = ({ children, styles = [] }: { children: ReactNode; styles?: StyleDeclarationValue[] }) => {
	const context = useAppDrawerContext();
	if (context == null) {
		throw new Error('SideDrawer must be used within AppDrawerProvider');
	}
	const { value } = context;
	return (
		<ToggleGroup.Root value={value} type='single' className={css(styleSheet.drawer, ...styles)}>
			{children}
		</ToggleGroup.Root>
	);
};

interface AppDrawerProviderProps {
	value?: string;
	onValueChange?: (value: string) => void;
	children: React.ReactNode;
	persistKey: string;
}

export const AppDrawerProvider = ({
	onValueChange,
	value: controlledValue,
	children,
	persistKey,
}: AppDrawerProviderProps) => {
	const [storedDrawerKey, persistActiveDrawerKey] = useLocalStorage<string>({
		key: persistKey,
		initialValue: '',
	});
	const [internalValue, setInternalValue] = useState(storedDrawerKey);
	const [storedDrawerWidth, setDrawerWidth] = useLocalStorage({
		key: LOCAL_STORAGE_APP_DRAWER_WIDTH_KEY,
		initialValue: String(MIN_DRAWER_WIDTH),
	});
	const drawerWidth = parseInt(storedDrawerWidth);

	const valueChangeRef = useRef(onValueChange);
	useEffect(() => {
		valueChangeRef.current = onValueChange;
	}, [onValueChange]);
	const handleValueChange = useCallback(
		(newValue: string) => {
			setInternalValue(newValue);
			persistActiveDrawerKey(newValue);
			if (valueChangeRef.current) {
				valueChangeRef.current(newValue);
			}
		},
		[persistActiveDrawerKey]
	);
	const handleWidthChange = useCallback(
		(newWidth: number) => {
			setDrawerWidth(String(newWidth));
		},
		[setDrawerWidth]
	);
	const value = controlledValue ?? internalValue;
	const state = useMemo(() => {
		return { value, setValue: handleValueChange, width: drawerWidth, setWidth: handleWidthChange };
	}, [value, handleValueChange, drawerWidth, handleWidthChange]);
	return <AppDrawerContext.Provider value={state}>{children}</AppDrawerContext.Provider>;
};

export const AdminDrawerProvider = (props: Omit<AppDrawerProviderProps, 'persistKey'>) => {
	return <AppDrawerProvider {...props} persistKey={LOCAL_STORAGE_ADMIN_ACTIVE_DRAWER_KEY} />;
};

export const CoffeeDrawerProvider = (props: Omit<AppDrawerProviderProps, 'persistKey'>) => {
	return <AppDrawerProvider {...props} persistKey={LOCAL_STORAGE_COFFEE_ACTIVE_DRAWER_KEY} />;
};

export const AppDrawer = (props: AppDrawerProps) => {
	return (
		<Portal destination={appDrawerPortalId}>
			<AppDrawerProvider
				value={props.value}
				onValueChange={props.onValueChange}
				persistKey={LOCAL_STORAGE_ACTIVE_APP_DRAWER_KEY}
			>
				<SideDrawer styles={props.styles}>{props.children}</SideDrawer>
			</AppDrawerProvider>
		</Portal>
	);
};

export const CoffeeDrawerToolbar = ({ children }: { children: React.ReactNode }) => {
	return (
		<div className={css(styleSheet.coffeeDrawerEdge)}>
			<div className={css(styleSheet.drawerToolbarContent)}>{children}</div>
		</div>
	);
};

export const AppDrawerToolbar = ({
	children,
	extendedFabButtons,
}: {
	children: React.ReactNode;
	extendedFabButtons?: React.ReactNode;
}) => {
	return (
		<div className={css(styleSheet.drawerEdge)}>
			<div className={css(styleSheet.drawerToolbarContent)}>{children}</div>
			<AppDrawerFab extendedFabButtons={extendedFabButtons} />
			<FabContext appearance={{ hidden: true }} />
		</div>
	);
};

export const AdminDrawerToolbar = ({ children }: { children: React.ReactNode }) => {
	return (
		<div className={css(styleSheet.drawerEdge)}>
			<div className={css(styleSheet.drawerToolbarContent)}>{children}</div>
		</div>
	);
};

export const AppDrawerToolbarButton = ({
	onClick,
	value,
	tooltip,
	...props
}: React.ComponentPropsWithoutRef<typeof ToggleGroup.Item> & {
	tooltip?: string;
}) => {
	const context = useAppDrawerContext();
	const handleClick: React.MouseEventHandler<HTMLButtonElement> = e => {
		onClick?.(e);
		if (!value) {
			return;
		}
		if (context.value !== value) {
			context.setValue(value);
			return;
		}
		context.setValue('');
	};
	if (tooltip) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span>
						<ToggleGroup.Item className={css(styleSheet.drawerButton)} onClick={handleClick} value={value} {...props} />
					</span>
				</TooltipTrigger>
				<TooltipContent side='left'>{tooltip}</TooltipContent>
			</Tooltip>
		);
	}
	return <ToggleGroup.Item className={css(styleSheet.drawerButton)} onClick={handleClick} value={value} {...props} />;
};

export const AppDrawerToolbarButtonAiAssistant = (
	props: Omit<React.ComponentProps<typeof AppDrawerToolbarButton>, 'children'>
) => {
	const userSession = useUserSession();
	if (!userSession.account.features?.contentGeneration?.enabled) {
		return null;
	}
	return (
		<AppDrawerToolbarButton {...props} tooltip='AI Assistant'>
			<SparklesIcon />
		</AppDrawerToolbarButton>
	);
};

export const AppDrawerSeparator = () => {
	return <Separator.Root className={css(styleSheet.drawerSeparator)} />;
};

export const AppDrawerContent = ({
	value,
	children,
	unmountOnExit = false,
	styles = [],
}: {
	value: string;
	children: React.ReactNode;
	unmountOnExit?: boolean;
	styles?: StyleDeclarationValue[];
}) => {
	const context = useAppDrawerContext();
	const showing = context.value === value;
	const drawerContentRef = useRef<HTMLDivElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const startXRef = useRef<number>(0);
	const startWidthRef = useRef<number>(0);
	const onResizerMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		setIsDragging(true);
		startXRef.current = e.clientX;
		if (drawerContentRef.current) {
			startWidthRef.current = drawerContentRef.current.offsetWidth;
		}
	};
	useEffect(() => {
		if (!isDragging) {
			return;
		}

		const handleMouseMove = (e: MouseEvent) => {
			const delta = startXRef.current - e.clientX;
			const newWidth = Math.min(Math.max(startWidthRef.current + delta, MIN_DRAWER_WIDTH), MAX_DRAWER_WIDTH);
			context.setWidth(newWidth);
		};

		const handleMouseUp = () => {
			setIsDragging(false);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging, context]);

	if (!showing && unmountOnExit) {
		return null;
	}

	return (
		<div
			className={css(styleSheet.drawerContent, !showing && styleSheet.drawerContentHidden, ...styles)}
			style={{ width: context.width }}
			ref={drawerContentRef}
		>
			{children}
			<div
				className={css(styleSheet.resizeHandle, isDragging && styleSheet.resizeHandleDragging)}
				onMouseDown={onResizerMouseDown}
				onDoubleClick={() => context.setWidth(MIN_DRAWER_WIDTH)}
				role='separator'
				aria-orientation='vertical'
				aria-label='Resize drawer'
			/>
		</div>
	);
};

/**
 * A special type of drawer content that maintains drawer state across pages.
 * If a sticky app drawer content is active on one page and user navigates to another page where the same sticky app drawer content is also present,
 * the drawer will remain open on the new page.
 */
export const StickyAppDrawerContent = (props: {
	value: string;
	children: React.ReactNode;
	unmountOnExit?: boolean;
	backgroundColor?: string;
}) => {
	const { setValue } = useAppDrawerContext();
	const [activeAppDrawerKey] = useLocalStorage({
		key: LOCAL_STORAGE_ACTIVE_APP_DRAWER_KEY,
	});
	useEffect(() => {
		if (props.value === activeAppDrawerKey) {
			setValue(props.value);
		}
	}, [props.value, activeAppDrawerKey, setValue]);
	return <AppDrawerContent {...props} />;
};
